import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CurrencyCode } from '@/lib/currency';
import { mergeCustomerProfiles } from '@/lib/customerProfiles';

/**
 * Invoices (ROADMAP 1E). Org-scoped CRUD over public.invoices /
 * public.invoice_line_items.
 *
 * The generated Supabase types (src/integrations/supabase/types.ts) don't yet
 * include the invoices tables — the data-model migration landed after the last
 * `supabase gen types` run — so we type rows locally and cast the client, the
 * same pattern CustomersAdmin uses for the customers table. Row access is still
 * governed by RLS ("Org members can manage invoices"); the `.eq(organization_id)`
 * filters here keep the client query scoped to the active org.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'void'
  | 'cancelled'
  | 'refunded';

export const INVOICE_STATUSES: { value: InvoiceStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'void', label: 'Void' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

export interface InvoiceListRow {
  id: string;
  invoice_number: string | null;
  status: InvoiceStatus;
  total_amount: number;
  amount_paid: number;
  amount_refunded: number;
  deposit_amount: number;
  currency_code: CurrencyCode;
  customer_name: string | null;
  due_date: string | null;
  created_at: string;
}

export interface InvoiceLineItemInput {
  id?: string;
  description: string;
  quantity: number;
  unit_amount: number;
  service_id?: string | null;
}

export interface InvoiceDetail {
  id: string;
  organization_id: string;
  customer_id: string | null;
  booking_id: string | null;
  invoice_number: string | null;
  status: InvoiceStatus;
  currency_code: CurrencyCode;
  subtotal_amount: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  amount_refunded: number;
  deposit_amount: number;
  customer_name: string | null;
  customer_email: string | null;
  notes: string | null;
  due_date: string | null;
  created_at: string;
  line_items: Array<InvoiceLineItemInput & { id: string; line_total: number }>;
}

export interface InvoiceLinkOptions {
  customers: Array<{ id: string; customer_id: string | null; name: string; email: string | null; phone: string | null }>;
  jobs: Array<{
    id: string;
    customer_id: string | null;
    client_email: string | null;
    client_phone: string | null;
    service_id: string | null;
    service_name: string | null;
    service_price: number | null;
    title: string;
    scheduled_date: string | null;
  }>;
}

export interface SaveInvoiceInput {
  id?: string | null;
  organization_id: string;
  customer_id: string | null;
  booking_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  invoice_number: string | null;
  status: InvoiceStatus;
  currency_code: CurrencyCode;
  tax_amount: number;
  due_date: string | null;
  notes: string | null;
  line_items: InvoiceLineItemInput[];
}

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/** List the active org's invoices, newest first. */
export function useInvoices(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['invoices', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<InvoiceListRow[]> => {
      const { data, error } = await db
        .from('invoices')
        .select(
          'id, invoice_number, status, total_amount, amount_paid, amount_refunded, deposit_amount, currency_code, customer_name, due_date, created_at'
        )
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as InvoiceListRow[];
    },
  });
}

/** Full invoice (header + line items) for the edit form. */
export function useInvoiceDetail(invoiceId: string | null) {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceDetail | null> => {
      const { data, error } = await db
        .from('invoices')
        .select('*, invoice_line_items(*)')
        .eq('id', invoiceId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const { invoice_line_items, ...header } = data;
      const items = (invoice_line_items ?? []) as Array<
        InvoiceDetail['line_items'][number] & { sort_order?: number }
      >;
      return {
        ...header,
        line_items: items.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
      } as InvoiceDetail;
    },
  });
}

/** Customers + jobs (bookings) the active org can link an invoice to. */
export function useInvoiceLinkOptions(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['invoice-link-options', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<InvoiceLinkOptions> => {
      const [customersRes, jobsRes, servicesRes, enquiriesRes] = await Promise.all([
        db
          .from('customers')
          .select('id, name, email, phone, address, created_at')
          .eq('organization_id', orgId)
          .order('name', { ascending: true }),
        db
          .from('jobs')
          .select('id, customer_id, service_id, client_name, client_email, client_phone, address, title, scheduled_date')
          .eq('organization_id', orgId)
          .order('scheduled_date', { ascending: false }),
        db
          .from('services')
          .select('id, name, price')
          .eq('organization_id', orgId)
          .eq('is_active', true),
        db
          .from('enquiries')
          .select('id, customer_id, client_name, client_email, client_phone, address, created_at')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false }),
      ]);

      if (customersRes.error) throw customersRes.error;
      if (jobsRes.error) throw jobsRes.error;
      if (servicesRes.error) throw servicesRes.error;
      if (enquiriesRes.error) throw enquiriesRes.error;

      const jobs = (jobsRes.data ?? []) as Array<{ id: string; customer_id: string | null; service_id: string | null; client_name: string | null; client_email: string | null; client_phone: string | null; address: string | null; title: string; scheduled_date: string | null }>;
      const services = (servicesRes.data ?? []) as Array<{ id: string; name: string | null; price: number | string | null }>;
      const servicesById = new Map(services.map((service) => [service.id, service]));
      const enquiries = (enquiriesRes.data ?? []) as Array<{ customer_id: string | null; client_name: string | null; client_email: string | null; client_phone: string | null; address: string | null; created_at: string | null }>;
      const customers = mergeCustomerProfiles([
        ...((customersRes.data ?? []) as Array<{ id: string; name: string | null; email: string | null; phone: string | null; address: string | null; created_at: string | null }>).map((customer) => ({
          customer_id: customer.id,
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          created_at: customer.created_at,
        })),
        ...jobs.map((job) => ({
          customer_id: job.customer_id,
          name: job.client_name,
          email: job.client_email,
          phone: job.client_phone,
          address: job.address,
          created_at: job.scheduled_date,
        })),
        ...enquiries.map((enquiry) => ({
          customer_id: enquiry.customer_id,
          name: enquiry.client_name,
          email: enquiry.client_email,
          phone: enquiry.client_phone,
          address: enquiry.address,
          created_at: enquiry.created_at,
        })),
      ]).map((customer) => ({
        id: customer.id,
        customer_id: customer.customer_id,
        name: customer.name,
        email: customer.email || null,
        phone: customer.phone,
      }));

      return {
        customers,
        jobs: jobs.map(({ id, customer_id, client_email, client_phone, service_id, title, scheduled_date }) => {
          const service = service_id ? servicesById.get(service_id) : undefined;
          const servicePrice = service?.price == null ? null : Number(service.price);
          return {
            id,
            customer_id,
            client_email,
            client_phone,
            service_id,
            service_name: service?.name ?? null,
            service_price: Number.isFinite(servicePrice) ? servicePrice : null,
            title,
            scheduled_date,
          };
        }),
      };
    },
  });
}

/**
 * Create or update an invoice and its line items in one call.
 *
 * Header totals (subtotal/total) are derived from the line items here so the
 * stored snapshot stays consistent. `line_total` is a generated column in the
 * DB, so it is intentionally never written. Editing replaces the line-item set
 * (delete-then-insert) — basic but sufficient for 1E.
 */
export function useSaveInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveInvoiceInput): Promise<{ id: string }> => {
      const subtotal = round2(
        input.line_items.reduce((sum, li) => sum + li.quantity * li.unit_amount, 0)
      );
      const tax = round2(input.tax_amount || 0);
      const total = round2(subtotal + tax);

      const header = {
        organization_id: input.organization_id,
        customer_id: input.customer_id,
        booking_id: input.booking_id,
        customer_name: input.customer_name,
        customer_email: input.customer_email,
        invoice_number: input.invoice_number,
        status: input.status,
        currency_code: input.currency_code,
        subtotal_amount: subtotal,
        tax_amount: tax,
        total_amount: total,
        due_date: input.due_date,
        notes: input.notes,
      };

      let invoiceId = input.id ?? null;

      if (invoiceId) {
        const { error } = await db.from('invoices').update(header).eq('id', invoiceId);
        if (error) throw error;

        const { error: deleteError } = await db
          .from('invoice_line_items')
          .delete()
          .eq('invoice_id', invoiceId);
        if (deleteError) throw deleteError;
      } else {
        const { data, error } = await db
          .from('invoices')
          .insert(header)
          .select('id')
          .single();
        if (error) throw error;
        invoiceId = data.id as string;
      }

      const rows = input.line_items.map((li, index) => ({
        invoice_id: invoiceId,
        organization_id: input.organization_id,
        service_id: li.service_id ?? null,
        description: li.description,
        quantity: li.quantity,
        unit_amount: li.unit_amount,
        sort_order: index,
      }));

      if (rows.length) {
        const { error } = await db.from('invoice_line_items').insert(rows);
        if (error) throw error;
      }

      return { id: invoiceId as string };
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', input.organization_id] });
      if (input.id) queryClient.invalidateQueries({ queryKey: ['invoice', input.id] });
    },
  });
}

interface DeliverInvoiceInput {
  invoiceId: string;
  organizationId: string;
}

/**
 * Email an invoice (with a server-generated PDF attached) to its snapshotted
 * customer. The `send-invoice` edge function authenticates the caller, verifies
 * org membership, resolves the recipient + content from the DB, and flips a draft
 * to `sent`. supabase-js attaches the signed-in user's JWT automatically, which
 * the function requires — there is no anonymous send path.
 */
export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: DeliverInvoiceInput,
    ): Promise<{ success?: boolean; skipped?: string; recipient?: string }> => {
      const { data, error } = await supabase.functions.invoke('send-invoice', {
        body: { invoiceId: input.invoiceId, organizationId: input.organizationId, mode: 'send' },
      });
      if (error) throw error;
      return (data ?? {}) as { success?: boolean; skipped?: string; recipient?: string };
    },
    onSuccess: (_result, input) => {
      // Status may have moved draft -> sent server-side.
      queryClient.invalidateQueries({ queryKey: ['invoices', input.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['invoice', input.invoiceId] });
    },
  });
}

interface CreateRefundInput {
  organizationId: string;
  invoiceId: string;
  amount?: number;
  reason: 'requested_by_customer' | 'duplicate' | 'fraudulent';
}

interface CreateRefundResult {
  configured?: boolean;
  refundId?: string;
  status?: string;
  amount?: number;
  error?: string;
}

/**
 * Issue a Stripe refund for a paid invoice via the `stripe-create-refund` edge
 * function. Mirrors `useSendInvoice`. On success, invalidates both the list query
 * and the detail query so the UI reflects the new `amount_refunded` value.
 */
export function useCreateRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRefundInput): Promise<CreateRefundResult> => {
      const { data, error } = await supabase.functions.invoke('stripe-create-refund', {
        body: {
          organizationId: input.organizationId,
          invoiceId: input.invoiceId,
          ...(input.amount !== undefined ? { amount: input.amount } : {}),
          reason: input.reason,
        },
      });
      if (error) throw error;
      const result = (data ?? {}) as CreateRefundResult;
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', input.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['invoice', input.invoiceId] });
    },
  });
}

/** Base64 PDF payload returned by the edge function in `download` mode. */
interface InvoicePdfPayload {
  filename: string;
  mimeType: string;
  pdfBase64: string;
}

/**
 * Fetch the same server-generated invoice PDF and trigger a browser download.
 * Reuses the `send-invoice` function in `download` mode so the downloaded file is
 * byte-identical to what a customer would receive; no email is sent.
 */
export function useDownloadInvoice() {
  return useMutation({
    mutationFn: async (input: DeliverInvoiceInput): Promise<string> => {
      const { data, error } = await supabase.functions.invoke('send-invoice', {
        body: { invoiceId: input.invoiceId, organizationId: input.organizationId, mode: 'download' },
      });
      if (error) throw error;

      const payload = data as InvoicePdfPayload | null;
      if (!payload?.pdfBase64) throw new Error('No PDF was returned');

      const bytes = Uint8Array.from(atob(payload.pdfBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: payload.mimeType || 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = payload.filename || 'invoice.pdf';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);

      return payload.filename;
    },
  });
}
